import { Stack } from '@mui/material';
import { Field, FieldProps } from 'formik';

import { useLocation } from '@redwoodjs/router';

import NPICurrencyInput from 'src/components/inputs/NPICurrencyInput/NPICurrencyInput';
import { NPICurrencyInputProps } from 'src/components/inputs/NPICurrencyInput/NPICurrencyInput.types';
import useFeatureFlag from 'src/hooks/useFeatureFlag';
import { getDescendantProp } from 'src/utils/utils';

import useHandleNPICurrencyInputFieldError from './useHandleNPICurrencyInputFieldError';

interface NPICurrencyInputFieldProps extends NPICurrencyInputProps {
    isDisabled?: boolean;
    fieldName: string;
    errorMessage?: string;
    dimensions?: {
        width?: string;
        height?: string;
        borderRadius?: string;
    };
}

/**
 * Integrates `NPICurrencyInput` with Forms
 ** Sets field value based on the fieldName, debounced after conversion
 ** Adds input errors (min/max) from `NPICurrencyInput` to form errors
 ** Integrates form validation when touched on blur
 ** Display form error messages underneath input
 ** For more information see: `NPICurrencyInputField.md`
 */
export function NPICurrencyInputField({
    fieldName,
    errorMessage,
    isDisabled,
    dimensions,
    error,
    ...props
}: NPICurrencyInputFieldProps) {
    const location = useLocation();
    const newFieldErrorNPICurrencyInputFieldEnabled = useFeatureFlag(
        'NEW_FIELD_ERROR_NPICURRENCYINPUTFIELD'
    );

    const {
        finalErrorMessage,
        hasError: hasErrorNew,
        inputErrorMessages,
        setInputErrorMessages,
    } = useHandleNPICurrencyInputFieldError({ errorMessage, fieldName });

    return (
        <Stack
            sx={{
                width: dimensions?.width ?? '100%',
                height: dimensions?.height,
                borderRadius: dimensions?.borderRadius,
            }}
        >
            <Field name={fieldName}>
                {({ field, form }: FieldProps) => {
                    const { setFieldValue, touched, errors, handleBlur } = form;

                    // Only show errors when field is touched
                    const isFieldTouched = getDescendantProp(touched, fieldName);
                    const fieldError = getDescendantProp(errors, fieldName);
                    const hasFormError = isFieldTouched && Boolean(fieldError);

                    const hasErrorOld = hasFormError || props?.error;

                    const hasError = newFieldErrorNPICurrencyInputFieldEnabled
                        ? hasErrorNew
                        : hasErrorOld;

                    const displayErrorMessage =
                        isFieldTouched && fieldError ? fieldError : errorMessage;

                    let isValueCurrency = false;

                    if (location.pathname.includes('merchant/add')) {
                        // FIXME: https://newpayment.atlassian.net/browse/NPI-2064
                        isValueCurrency = field.value - Math.floor(field.value) > 0;
                    }

                    return (
                        <>
                            <NPICurrencyInput
                                name={field.name}
                                defaultValueInCurrency={isValueCurrency ? field.value : undefined}
                                defaultValueInCents={
                                    !isValueCurrency ? (field.value ?? 0) : undefined
                                }
                                disabled={isDisabled}
                                handleValue={(valueInCents: number) => {
                                    const parsedValue = Number.isFinite(valueInCents)
                                        ? valueInCents
                                        : 0;

                                    if (field.value !== parsedValue) {
                                        setFieldValue(field.name, parsedValue);
                                    }
                                }}
                                sx={{
                                    height: dimensions?.height,
                                    borderRadius: dimensions?.borderRadius,
                                }}
                                touched={isFieldTouched}
                                error={error || hasError}
                                errorMessage={inputErrorMessages}
                                setErrorMessage={setInputErrorMessages}
                                onBlur={handleBlur}
                                {...props}
                            />
                            {!newFieldErrorNPICurrencyInputFieldEnabled &&
                                hasError &&
                                isFieldTouched &&
                                displayErrorMessage && (
                                    <label className="error">{displayErrorMessage}</label>
                                )}
                            {newFieldErrorNPICurrencyInputFieldEnabled &&
                                hasError &&
                                isFieldTouched &&
                                finalErrorMessage?.length > 0 && (
                                    <ul
                                        style={{
                                            listStyle: 'disc',
                                            marginBlockStart: '0px',
                                            marginBlockEnd: '0px',
                                            paddingInlineStart: '18px',
                                        }}
                                    >
                                        {finalErrorMessage.map(
                                            (errorMessage: string, index: number) => {
                                                return (
                                                    <li
                                                        key={`${index}-${fieldName}`}
                                                        className="error"
                                                    >
                                                        {errorMessage}
                                                    </li>
                                                );
                                            }
                                        )}
                                    </ul>
                                )}
                        </>
                    );
                }}
            </Field>
        </Stack>
    );
}

export default NPICurrencyInputField;
